import React, { useCallback, useMemo, useState } from 'react';
import {
  Button as CarbonButton,
  Dropdown,
  Tag,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  TextArea,
  Toggle,
  Tooltip,
} from '@carbon/react';
import {
  CollapseAll,
  Draggable,
  ExpandAll,
  Filter,
  Information,
  Laptop,
  Screen,
  TrashCan,
  WarningAltFilled,
} from '@carbon/react/icons';
import {
  aliasGroupSize,
  canDrop,
  distinctNameCount,
  includedTypeBreakdown,
  isApproachingOperationCap,
  MAX_OPERATION_ENTITIES,
  nameAliasComposition,
  nameHasFlaggedMember,
  nameParticipation,
  operationEntityCount,
  sortUnactioned,
  survivorHasDependents,
  survivorKeys as survivorKeysOf,
  unactionedCount,
  type ConsolidationEntityType,
  type ConsolidationName,
  type ConsolidationState,
  type ConsolidationAction,
  type SortMode,
} from './consolidation-utils';

const SORT_ITEMS: Array<{ id: SortMode; label: string }> = [
  { id: 'confidence-desc', label: 'Confidence (high → low)' },
  { id: 'confidence-asc', label: 'Confidence (low → high)' },
  { id: 'alpha-asc', label: 'Name (A → Z)' },
  { id: 'alpha-desc', label: 'Name (Z → A)' },
];
import { ENTITY_TYPE_LABEL } from 'pages/documents/structuring/reconcile/types';
import { WarningCircleIcon } from 'pages/documents/structuring/reconcile/warning-circle-icon';
import styles from './entity-consolidation-panel.module.scss';

interface Props {
  state: ConsolidationState;
  dispatch: (action: ConsolidationAction) => void;
  // The distinct-name selection (§3.4) is CONTROLLED by the parent: this is the
  // current set (click selects one, shift-click a contiguous range, ctrl/cmd-click
  // toggles one) and onSelectKeys is the only writer. The parent highlights every
  // selected name's left-pane rows and the set is the drag payload — dragging any
  // selected name drags them all onto a survivor. A document row selected in a
  // survivor's View Documents table clears this (the parent owns both), so the two
  // highlight surfaces can never be lit at once.
  selectedKeys: ReadonlySet<string>;
  onSelectKeys: (keys: string[]) => void;
  // R12: the per-row funnel steers the LEFT candidate list to a single distinct
  // name's members (null clears). The parent owns the filtered candidate set and
  // the "filtered" banner; the panel only reports the toggled key + reflects the
  // active one on the funnel. Optional so surfaces without a left table can omit it.
  onFilterName?: (key: string | null) => void;
  activeFilterKey?: string | null;
  // Removing a survivor that still has deduped names beneath it needs the §3.3
  // confirmation. When provided, the host owns that (Carbon) modal and this fires
  // it; when omitted (surfaces without the modal), the trashcan falls back to a
  // native confirm so the guard is never skipped.
  onConfirmRemoveName?: (key: string) => void;
}

// The "2 Organization · 1 Imported" style breakdown of a type-count record.
const typeBreakdownLabel = (
  counts: Record<ConsolidationEntityType, number>
): string =>
  (Object.keys(counts) as ConsolidationEntityType[])
    .filter((type) => counts[type] > 0)
    .map((type) => `${counts[type]} ${ENTITY_TYPE_LABEL[type]}`)
    .join(' · ') || 'none';

// The "2 Organization · 1 Imported" style breakdown of a name's included members.
const breakdownLabel = (
  state: ConsolidationState,
  name: ConsolidationName
): string => typeBreakdownLabel(includedTypeBreakdown(state, name));

// The right-hand "Entity Consolidation" panel (narrative §3.1–3.7), rendered as
// the Name Preservation data table: a column header row (Survivor · Distinct
// Entity Name · Entity Count · Alias Group · Actions), a toolbar (search / sort /
// layout), survivors at top with their deduplicated names nested beneath, a live
// "N distinct names, x unactioned" indicator, a per-row funnel that filters the
// left list, and deferred rationale entry for every survivor past the first. Drag
// a name row onto a survivor to deduplicate it (native HTML5 DnD; the equivalent
// checkbox path lives on the left panel).
const EntityConsolidationPanel: React.FC<Props> = ({
  state,
  dispatch,
  selectedKeys,
  onSelectKeys,
  onFilterName,
  activeFilterKey = null,
  onConfirmRemoveName,
}) => {
  const [search, setSearch] = useState('');
  const [showDeduped, setShowDeduped] = useState(true);
  // Whether removed (hidden) unactioned names are revealed for restoring (§3.7).
  const [showHidden, setShowHidden] = useState(false);
  // Wide mode splits the Alias Group column out into its own column (§3.7 / R15);
  // condensed folds it back into the name cell to save horizontal space.
  const [wideMode, setWideMode] = useState(true);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  // The names currently being dragged (§3.4). Set on drag start so every
  // survivor row can be evaluated as a drop target up front (R3): invalid
  // targets grey out instead of only refusing the drop.
  const [draggingKeys, setDraggingKeys] = useState<string[] | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('confidence-desc');
  // The anchor is the last row a plain/ctrl click landed on, used as one end of a
  // shift-range. The selection itself is controlled by the parent (selectedKeys).
  const [anchorKey, setAnchorKey] = useState<string | null>(null);

  const filterable = typeof onFilterName === 'function';

  // The keys still present in the model — the valid subset a selection is allowed
  // to reference. Stale keys from the parent (a name that has since left the
  // model) are filtered out before every write, so they can't be dragged or
  // re-highlighted; the parent's derivations ignore them too.
  const liveKeys = useMemo(
    () => new Set(state.names.map((n) => n.key)),
    [state.names]
  );

  // Report a selection change up to the parent (the only writer), always with the
  // valid (live) subset.
  const notifySelection = useCallback(
    (keys: Set<string>) => {
      onSelectKeys([...keys].filter((k) => liveKeys.has(k)));
    },
    [onSelectKeys, liveKeys]
  );

  const survivorKeys = useMemo(() => survivorKeysOf(state), [state]);

  // R22: how many survivors alias together (0 when fewer than two) — drives the
  // per-survivor alias-group count (§3.7).
  const aliasCount = useMemo(() => aliasGroupSize(state), [state]);

  // R17: operation-cap affordance. The count of entities this reconcile would
  // touch (the deduped survivor+members union the backend gates); the panel warns
  // as it nears MAX_OPERATION_ENTITIES and hard-blocks at/over it.
  const opCount = useMemo(() => operationEntityCount(state), [state]);
  const overCap = opCount > MAX_OPERATION_ENTITIES;
  const approachingCap = useMemo(
    () => isApproachingOperationCap(state),
    [state]
  );

  // R3: a survivor is an invalid drop target for the in-flight drag when the
  // model's canDrop rejects it — grey it out rather than accept-then-refuse.
  const isInvalidDropTarget = useCallback(
    (survivorKey: string) =>
      draggingKeys !== null && !canDrop(state, draggingKeys, survivorKey),
    [draggingKeys, state]
  );

  // Deduped names bucketed by their survivor — one pass instead of a scan per
  // survivor row (drag hover re-renders frequently via setDragOverKey).
  const dedupedByKey = useMemo(() => {
    const map = new Map<string, ConsolidationName[]>();
    state.names.forEach((n) => {
      if (n.dedupedInto) {
        const list = map.get(n.dedupedInto) ?? [];
        list.push(n);
        map.set(n.dedupedInto, list);
      }
    });
    return map;
  }, [state]);
  const dedupedUnder = (survivorKey: string) =>
    dedupedByKey.get(survivorKey) ?? [];

  // Ordered rows: each survivor followed by its deduped names, then the
  // unactioned names. A survivor always stays visible even when filtered out by
  // search (it must remain a drop target).
  const term = search.trim().toLowerCase();
  const unactioned = useMemo(
    () =>
      state.names.filter(
        (n) =>
          n.role === 'unactioned' &&
          !n.hidden &&
          (term === '' || n.label.toLowerCase().includes(term))
      ),
    [state, term]
  );
  const hiddenNames = useMemo(
    () => state.names.filter((n) => n.role === 'unactioned' && n.hidden),
    [state]
  );
  const hiddenCount = hiddenNames.length;
  // Sort applies only to unactioned names (§3.1 / #30); survivors + their deduped
  // names keep their structural (designation) order.
  const sortedUnactioned = useMemo(
    () => sortUnactioned(state, unactioned, sortMode),
    [state, unactioned, sortMode]
  );

  // The keys in on-screen order (survivors, each followed by its deduped names,
  // then the sorted unactioned names) — the axis a shift-range is measured on.
  const orderedVisibleKeys = useMemo(() => {
    const keys: string[] = [];
    survivorKeys.forEach((key) => {
      keys.push(key);
      (dedupedByKey.get(key) ?? []).forEach((n) => keys.push(n.key));
    });
    sortedUnactioned.forEach((n) => keys.push(n.key));
    return keys;
  }, [survivorKeys, dedupedByKey, sortedUnactioned]);

  // Column count for full-width rows (section labels, rationale detail, empty
  // states): Survivor · flag · Name · Entity Count · [Alias Group] · Actions.
  const colCount = wideMode ? 6 : 5;

  // Click / shift-click / ctrl-click name selection (§3.4). Plain click on the
  // sole selected row clears it (a toggle), matching the left-pane highlight.
  const handleRowClick = useCallback(
    (key: string, e: React.MouseEvent) => {
      let next: Set<string>;
      if (e.shiftKey && anchorKey) {
        const from = orderedVisibleKeys.indexOf(anchorKey);
        const to = orderedVisibleKeys.indexOf(key);
        if (from === -1 || to === -1) {
          next = new Set([key]);
        } else {
          const [lo, hi] = from < to ? [from, to] : [to, from];
          next = new Set(orderedVisibleKeys.slice(lo, hi + 1));
        }
      } else if (e.metaKey || e.ctrlKey) {
        next = new Set(selectedKeys);
        if (next.has(key)) next.delete(key);
        else next.add(key);
      } else {
        next =
          selectedKeys.size === 1 && selectedKeys.has(key)
            ? new Set()
            : new Set([key]);
      }
      notifySelection(next);
      if (!e.shiftKey) setAnchorKey(key);
    },
    [anchorKey, orderedVisibleKeys, selectedKeys, notifySelection]
  );

  // Dragging a selected row drags every selected name that can actually be
  // deduplicated (survivors are highlight-only, never a drag source); dragging
  // an unselected row drags just that one.
  const handleDragStart = (key: string, e: React.DragEvent) => {
    const dragKeys =
      selectedKeys.has(key) && selectedKeys.size > 1
        ? orderedVisibleKeys.filter(
            (k) =>
              selectedKeys.has(k) &&
              state.names.find((n) => n.key === k)?.role !== 'survivor'
          )
        : [key];
    e.dataTransfer.setData('text/plain', JSON.stringify(dragKeys));
    setDraggingKeys(dragKeys);
  };

  const handleDragEnd = () => {
    setDraggingKeys(null);
    setDragOverKey(null);
  };

  const handleDrop = (survivorKey: string, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverKey(null);
    setDraggingKeys(null);
    const raw = e.dataTransfer.getData('text/plain');
    if (!raw) return;
    let keys: string[];
    try {
      const parsed: unknown = JSON.parse(raw);
      keys = Array.isArray(parsed) ? (parsed as string[]) : [raw];
    } catch {
      keys = [raw];
    }
    if (keys.length === 0) return;
    // §3.4 all-or-nothing across a multi-name drag is enforced in the model.
    dispatch({ type: 'drag', keys, survivorKey });
    onSelectKeys([]);
  };

  // §3.3: resetting a survivor that still has deduplicated names beneath it must
  // be confirmed first. Returns true when the caller should defer to that
  // confirmation (host modal shown, or the native fallback declined) instead of
  // dispatching its reset immediately. Shared by the trashcan and the
  // survivor-toggle-off, which both reset the survivor via the model.
  const deferForSurvivorReset = (key: string): boolean => {
    if (!survivorHasDependents(state, key)) return false;
    if (onConfirmRemoveName) {
      onConfirmRemoveName(key);
      return true;
    }
    return !window.confirm(
      'This is the last remaining entity for this survivor. Removing it will reset the survivor and remove every deduplicated name grouped beneath it.'
    );
  };

  const rowClass = (name: ConsolidationName) =>
    [
      styles.row,
      name.role === 'survivor' && styles.survivorRow,
      name.role === 'deduped' && styles.dedupedRow,
      name.hidden && styles.hiddenRow,
      selectedKeys.has(name.key) && styles.highlighted,
      dragOverKey === name.key && styles.dropTarget,
      // R3: grey out a survivor that can't accept the current drag.
      name.role === 'survivor' &&
        isInvalidDropTarget(name.key) &&
        styles.dropInvalid,
    ]
      .filter(Boolean)
      .join(' ');

  const toggleFilter = (key: string) =>
    onFilterName?.(activeFilterKey === key ? null : key);

  const renderRow = (name: ConsolidationName): React.ReactNode => {
    const isSurvivor = name.role === 'survivor';
    const draggable = name.role !== 'survivor';
    const survivorIndex = isSurvivor ? survivorKeys.indexOf(name.key) : -1;
    const totalMembers = name.memberIds.length;
    const includedMembers = name.checkedIds.length;
    // §3.7 alias-group composition (correct column) + Decision #31 name-level
    // participation sums. Composition is a fixed property of ALL members; the
    // participation sum is over CHECKED members only.
    const composition = nameAliasComposition(state, name);
    const participation = nameParticipation(state, name);
    const filterActive = activeFilterKey === name.key;

    // §3.7: the column describes what the alias family (or families) under this
    // name actually contain — e.g. "2 Individual" — not the name's own checked
    // members (that's the Entity Count column). "—" when the name has no alias
    // group (Erik feedback).
    const aliasGroupCell =
      composition.aliasGroupCount > 0 ? (
        <span
          className={styles.aliasComposition}
          data-testid={`alias-composition-${name.key}`}
        >
          {typeBreakdownLabel(composition.typeBreakdown)}
        </span>
      ) : (
        <span className={styles.muted}>—</span>
      );

    return (
      <React.Fragment key={name.key}>
        <tr
          className={rowClass(name)}
          data-selected={selectedKeys.has(name.key) ? 'true' : undefined}
          draggable={draggable}
          onDragStart={(e) => handleDragStart(name.key, e)}
          onDragEnd={draggable ? handleDragEnd : undefined}
          onDragOver={
            isSurvivor
              ? (e) => {
                  // R3: only a valid target accepts the drop (preventDefault) and
                  // highlights; an invalid one stays greyed and rejects the drop.
                  if (isInvalidDropTarget(name.key)) return;
                  e.preventDefault();
                  setDragOverKey(name.key);
                }
              : undefined
          }
          onDragLeave={isSurvivor ? () => setDragOverKey(null) : undefined}
          onDrop={
            isSurvivor && !isInvalidDropTarget(name.key)
              ? (e) => handleDrop(name.key, e)
              : undefined
          }
          onClick={(e) => handleRowClick(name.key, e)}
        >
          {/* Survivor toggle */}
          <td className={styles.survivorCell}>
            <Toggle
              id={`survivor-${name.key}`}
              size="sm"
              labelText=""
              labelA=""
              labelB=""
              hideLabel
              toggled={isSurvivor}
              onToggle={() => {
                // Toggling a survivor off resets it and its deduped names, so it
                // needs the same §3.3 confirmation the trashcan uses.
                if (deferForSurvivorReset(name.key)) return;
                dispatch({ type: 'toggleSurvivor', key: name.key });
              }}
              aria-label={`Keep ${name.label} as a surviving entity`}
            />
          </td>
          {/* Address-confidence flag column (#26) — the yellow warning marker
              (mock default-deduplication/v25), a hover tooltip one focus away. */}
          <td className={styles.flagCell}>
            {nameHasFlaggedMember(state, name) && (
              <Tooltip
                align="bottom"
                autoAlign
                label="An included entity has an unresolved address-confidence flag — resolve or remove it before reconciling."
              >
                <button
                  type="button"
                  className={styles.flagTrigger}
                  aria-label="Address confidence issue"
                >
                  <WarningCircleIcon className={styles.flagIcon} />
                </button>
              </Tooltip>
            )}
          </td>
          {/* Distinct Entity Name */}
          <td className={styles.nameCell}>
            <span className={styles.nameMain}>
              {/* Survivor/alias chips sit on their own line above the name. */}
              {isSurvivor && (
                <span className={styles.badges}>
                  <Tag type="high-contrast" size="sm">
                    {`Survivor ${survivorIndex + 1}`}
                  </Tag>
                  {/* R22: alias-group membership chip, always inline next to the
                      Survivor chip in the Distinct Entity Name column. */}
                  {aliasCount >= 2 && (
                    <Tag type="teal" size="sm">
                      Alias
                    </Tag>
                  )}
                </span>
              )}
              {/* Deduped rows carry the drag/nested icon inline, left of the name
                  (Erik feedback), so the row loses a vertical layer. */}
              <span className={styles.nameLine}>
                {draggable && name.role === 'deduped' && (
                  <Draggable
                    size={16}
                    className={styles.dragHandle}
                    aria-hidden
                  />
                )}
                <span className={styles.name}>{name.label}</span>
              </span>
            </span>
            <span className={styles.subtle}>{breakdownLabel(state, name)}</span>
          </td>
          {/* Entity Count */}
          <td className={styles.countCell}>
            <span className={styles.count}>
              {includedMembers} of {totalMembers} included
            </span>
            {includedMembers < totalMembers && name.role !== 'unactioned' && (
              <CarbonButton
                kind="ghost"
                size="sm"
                className={styles.includeAll}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  dispatch({ type: 'includeAll', key: name.key });
                }}
              >
                Include all
              </CarbonButton>
            )}
            {includedMembers > 0 &&
              (participation.workspaceCount > 0 ||
                participation.interpretationCount > 0) && (
                <span
                  className={styles.participation}
                  data-testid={`name-participation-${name.key}`}
                >
                  {`Workspaces: ${participation.workspaceCount} · Interpretations: ${participation.interpretationCount}`}
                </span>
              )}
          </td>
          {/* Alias Group (wide mode only) — the per-name alias-group composition
              (§3.7). The survivor's own alias-group membership chip lives inline
              in the name column, not here. */}
          {wideMode && <td className={styles.aliasCell}>{aliasGroupCell}</td>}
          {/* Actions */}
          <td className={styles.actionsCell}>
            {name.hidden ? (
              <CarbonButton
                kind="ghost"
                size="sm"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  dispatch({ type: 'restoreName', key: name.key });
                }}
              >
                Restore
              </CarbonButton>
            ) : (
              <CarbonButton
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={TrashCan}
                iconDescription="Remove"
                tooltipPosition="left"
                className={styles.remove}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  // §3.3 guard shared with the survivor toggle-off.
                  if (deferForSurvivorReset(name.key)) return;
                  dispatch({ type: 'removeName', key: name.key });
                }}
              />
            )}
            {filterable && !name.hidden && (
              <CarbonButton
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Filter}
                iconDescription={
                  filterActive
                    ? 'Clear table filter'
                    : `Filter table to ${name.label}`
                }
                tooltipPosition="left"
                className={
                  filterActive
                    ? `${styles.filterBtn} ${styles.filterBtnActive}`
                    : styles.filterBtn
                }
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  toggleFilter(name.key);
                }}
              />
            )}
          </td>
        </tr>

        {/* Deferred rationale for every survivor past the first (§3.6). */}
        {isSurvivor && survivorIndex > 0 && (
          <tr className={styles.detailRow}>
            <td colSpan={colCount} onClick={(e) => e.stopPropagation()}>
              <TextArea
                id={`rationale-${name.key}`}
                labelText="Why keep this as a separate entity? (required)"
                placeholder="Rationale for aliasing instead of merging"
                rows={1}
                value={name.rationale}
                invalid={name.rationale.trim() === ''}
                invalidText="A rationale is required for each additional surviving entity."
                onChange={(e) =>
                  dispatch({
                    type: 'setRationale',
                    key: name.key,
                    text: e.target.value,
                  })
                }
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h4 className={styles.heading}>
          Entity Consolidation
          <Tooltip
            align="bottom"
            autoAlign
            label="Deduplication is the default. Toggle a name to keep it as a separate (aliased) survivor; drag a name onto a survivor to deduplicate it in."
          >
            <button
              type="button"
              className={styles.infoBtn}
              aria-label="How to use Entity Consolidation"
            >
              <Information size={16} />
            </button>
          </Tooltip>
          <Tag type="cool-gray" size="sm" className={styles.countChip}>
            {distinctNameCount(state)} distinct names, {unactionedCount(state)}{' '}
            unactioned
          </Tag>
        </h4>
      </div>

      {/* Toolbar rendered flush with the table (Carbon TableToolbar), matching
          the mock's Name Preservation layout. */}
      <TableToolbar
        size="sm"
        className={styles.toolbar}
        aria-label="Consolidation table toolbar"
      >
        <TableToolbarContent>
          <TableToolbarSearch
            persistent
            placeholder="Search distinct names"
            value={search}
            onChange={(e) =>
              setSearch((e as React.ChangeEvent<HTMLInputElement>).target.value)
            }
            onClear={() => setSearch('')}
          />
          <Dropdown
            id="consolidation-sort"
            size="sm"
            type="inline"
            titleText="Sort"
            hideLabel
            label="Sort"
            items={SORT_ITEMS}
            itemToString={(item) => (item ? item.label : '')}
            selectedItem={SORT_ITEMS.find((i) => i.id === sortMode)}
            onChange={({ selectedItem }) =>
              selectedItem && setSortMode(selectedItem.id)
            }
          />
          <CarbonButton
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={wideMode ? Laptop : Screen}
            iconDescription={
              wideMode
                ? 'Condensed layout (fold the Alias Group column in)'
                : 'Wide layout (split the Alias Group column out)'
            }
            tooltipPosition="bottom"
            onClick={() => setWideMode((v) => !v)}
          />
          <CarbonButton
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={showDeduped ? CollapseAll : ExpandAll}
            iconDescription={
              showDeduped
                ? 'Condense deduplicated names into their survivors'
                : 'Expand deduplicated names'
            }
            tooltipPosition="bottom"
            onClick={() => setShowDeduped((v) => !v)}
          />
        </TableToolbarContent>
      </TableToolbar>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              <th scope="col" className={styles.survivorCol}>
                <span className={styles.thWithTip}>
                  Survivor
                  <Tooltip
                    align="bottom"
                    autoAlign
                    label="Toggle on to keep this distinct name as a separate, aliased surviving entity. Off (the default) deduplicates it into a survivor."
                  >
                    <button
                      type="button"
                      className={styles.infoBtn}
                      aria-label="About the Survivor column"
                    >
                      <Information size={16} />
                    </button>
                  </Tooltip>
                </span>
              </th>
              <th scope="col" className={styles.flagCol} aria-label="Flags" />
              <th scope="col">Distinct Entity Name</th>
              <th scope="col">Entity Count</th>
              {wideMode && <th scope="col">Alias Group</th>}
              <th scope="col" className={styles.actionsCol}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {survivorKeys.map((key) => {
              const survivor = state.names.find((n) => n.key === key)!;
              const deduped = dedupedUnder(key);
              return (
                <React.Fragment key={key}>
                  {renderRow(survivor)}
                  {showDeduped
                    ? deduped.map(renderRow)
                    : deduped.length > 0 && (
                        <tr className={styles.collapsedRow}>
                          <td
                            colSpan={colCount}
                            className={styles.collapsedCount}
                          >
                            {deduped.length} deduplicated{' '}
                            {deduped.length === 1 ? 'name' : 'names'} hidden
                          </td>
                        </tr>
                      )}
                </React.Fragment>
              );
            })}

            {/* Unactioned names follow the survivors directly — no section
                header (§3.7): they are just the still-unactioned distinct names.
                Removing one hides it, recoverable via "Show hidden names". */}
            {sortedUnactioned.map(renderRow)}

            {hiddenCount > 0 && (
              <tr className={styles.controlRow}>
                <td colSpan={colCount}>
                  <CarbonButton
                    kind="ghost"
                    size="sm"
                    onClick={() => setShowHidden((v) => !v)}
                  >
                    {showHidden
                      ? `Hide ${hiddenCount} removed ${
                          hiddenCount === 1 ? 'name' : 'names'
                        }`
                      : `Show ${hiddenCount} removed ${
                          hiddenCount === 1 ? 'name' : 'names'
                        }`}
                  </CarbonButton>
                </td>
              </tr>
            )}
            {showHidden && hiddenNames.map(renderRow)}
          </tbody>
        </table>
      </div>

      {/* R17: approaching-cap warning (non-blocking) → hard-cap block at/over the
          limit. The blocking case mirrors the server-side orchestrator rejection
          and the matching 'operation-cap' submitBlocker. */}
      {approachingCap && (
        <div
          className={overCap ? styles.capNoticeError : styles.capNotice}
          role={overCap ? 'alert' : 'status'}
          data-testid="operation-cap-notice"
        >
          <WarningAltFilled size={16} className={styles.capIcon} aria-hidden />
          <span>
            {overCap
              ? `This reconciliation includes ${opCount} entities, over the ${MAX_OPERATION_ENTITIES}-entity limit for a single operation. Remove some entities, or use bulk deduplication for larger consolidations.`
              : `Approaching the ${MAX_OPERATION_ENTITIES}-entity limit for a single reconciliation (${opCount} of ${MAX_OPERATION_ENTITIES} included). Larger consolidations belong in bulk deduplication.`}
          </span>
        </div>
      )}
    </div>
  );
};

export default EntityConsolidationPanel;
